import {alpha, CircularProgress, Divider, Menu, MenuItem, MenuProps, styled} from '@mui/material';
import {useAuth} from '../../contexts/auth/context';
import {translations} from '../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {useContext, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {ScreenPaths} from '../../enums/screen-path';
import {SnackBarContext} from '../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../contexts/snack-bar/types';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ExitIcon from '@mui/icons-material/DisabledByDefault';
import {RoleDropdown} from '../role-dropdown/role-dropdown';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import {RolePermissionType} from '../../backend/enums/role-permission-type';
import {AppVersionText} from '../app-version-text/app-version-text';
import {useAppContext} from '../../contexts/app/context';
import {exitToSocialApp} from '../../utils/social-app/social-app-launch';

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({theme}) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.primary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

/**
 * User menu used on the office header.
 */
export default function UserMenu() {
  // Anchor
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Hold state
  const [loggingOut, setLoggingOut] = useState<boolean>(false);

  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const {state: authState, signOut, hasReadPermission} = useAuth();
  const appContext = useAppContext();
  const canReadDailyCareLog = hasReadPermission(RolePermissionType.DailyCareLog);

  // Translations
  const {t} = useTranslation();

  // Get history and location
  let location = useLocation();
  let history = useHistory();

  // Go to the profile screen
  const handleProfile = () => {
    // Close Menu
    handleClose();

    history.push(`${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_Profile}`);
  };

  // Go to the Daily Care Log screen
  const handleDailyCareLog = () => {
    // Close Menu
    handleClose();

    history.push(
      `${ScreenPaths.DailyCareLog}/${authState.session?.selectedAffiliation?.entity.id}`
    );
  };

  // Go to the Office screen
  const handleOffice = () => {
    // Close Menu
    handleClose();

    history.push(
      `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}`
    );
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      // Start loading
      setLoggingOut(true);

      // Logout
      await signOut();
    } catch (error) {
      // Stop loading
      setLoggingOut(false);

      // Close menu
      handleClose();

      // Show toast
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.user_menu.errors.logout_error_message),
      });
    }
  };

  const dropdownInfo = {
    title:
      authState.session && authState.session.currentUser.displayName
        ? authState.session.currentUser.displayName
        : '',
    picture:
      authState.session && authState.session.currentUser.picture
        ? authState.session.currentUser.picture
        : '',
    defaultIconName: PersonOutlineIcon,
  };

  return (
    <>
      {authState.firebaseUser ? (
        <RoleDropdown
          info={dropdownInfo}
          active={open}
          showTitle={false}
          handleClick={handleClick}
          handleClose={handleClose}
        />
      ) : undefined}
      <StyledMenu id="user-menu-list" anchorEl={anchorEl} open={open} onClose={handleClose}>
        {canReadDailyCareLog && [
          location.pathname.startsWith(ScreenPaths.Office) && (
            <MenuItem key={ScreenPaths.Office} onClick={handleDailyCareLog} disableRipple>
              <ChecklistRtlIcon />
              {t(translations.user_menu.dailyCareLog as string)}
            </MenuItem>
          ),
          location.pathname.startsWith(ScreenPaths.DailyCareLog) && (
            <MenuItem key={ScreenPaths.DailyCareLog} onClick={handleOffice} disableRipple>
              <AppRegistrationIcon />
              {t(translations.user_menu.office as string)}
            </MenuItem>
          ),

          <Divider key="user-menu-divider" sx={{my: 0.5}} />,
        ]}

        <MenuItem onClick={handleProfile} disableRipple>
          <AccountCircleIcon />
          {t(translations.user_menu.profile as string)}
        </MenuItem>

        <Divider sx={{my: 0.5}} />

        <MenuItem onClick={handleLogout} disableRipple disabled={loggingOut}>
          {loggingOut ? (
            <CircularProgress color="inherit" size={18} sx={{marginRight: '12px'}} />
          ) : (
            <LogoutIcon />
          )}
          {t(translations.user_menu.logout as string)}
        </MenuItem>

        {appContext.state.runningFromSocialApp && (
          <>
            <Divider sx={{my: 0.5}} />

            <MenuItem
              disableRipple
              sx={{
                color: 'gray.lighter',
                backgroundColor: 'rgb(205 20 32 / 80%)',

                '&:hover': {
                  backgroundColor: 'rgb(205 20 32 / 95%)',
                  color: 'white',
                },
              }}
              onClick={async () => {
                try {
                  await handleLogout();
                } finally {
                  exitToSocialApp();
                }
              }}
              disabled={loggingOut}
            >
              {loggingOut ? (
                <CircularProgress color="inherit" size={18} sx={{marginRight: '12px'}} />
              ) : (
                <ExitIcon style={{color: 'inherit !important'}} />
              )}
              {t(translations.office.generics.exit)}
            </MenuItem>
          </>
        )}

        <AppVersionText sx={{p: '0px 10px'}} />
      </StyledMenu>
    </>
  );
}
